import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Send, Clock, CalendarDays, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import MonthlyRevenue from "@/components/account/MonthlyRevenue";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h) => {
  const ampm = h < 12 ? "AM" : "PM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${String(h).padStart(2, "0")}:00 (${hr} ${ampm})`;
};

function useTimezones() {
  return useMemo(() => {
    try {
      if (typeof Intl.supportedValuesOf === "function") return Intl.supportedValuesOf("timeZone");
    } catch { /* noop */ }
    return ["UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
      "Europe/London", "Europe/Paris", "Europe/Berlin", "Asia/Kolkata", "Asia/Dubai",
      "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney"];
  }, []);
}

const selectCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-200";

function Row({ title, desc, children, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100" data-testid={testid}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900">{title}</p>
        {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

export default function DigestSettings() {
  const timezones = useTimezones();
  const [state, setState] = useState(null);     // null=loading, false=forbidden(hide), object=loaded
  const [loadErr, setLoadErr] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => {
    setLoadErr(false);
    api.get("/account/digest")
      .then(({ data }) => {
        const prefs = { ...data.prefs };
        if (!data.prefs?.timezone || data.prefs.timezone === "UTC") {
          try { prefs.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { prefs.timezone = "UTC"; }
        }
        setState({ ...data, prefs });
      })
      .catch((e) => {
        if (e.response?.status === 403) setState(false);   // not an operator — hide entirely
        else { setState(false); setLoadErr(true); }        // transient error — offer a retry
      });
  };

  useEffect(() => { load(); }, []);

  if (loadErr) return (
    <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 flex items-center justify-between gap-4" data-testid="digest-load-error">
      <p className="text-sm text-slate-600">Couldn't load your sales-digest settings right now.</p>
      <Button variant="outline" size="sm" onClick={load} data-testid="digest-retry">Retry</Button>
    </section>
  );
  if (!state) return null;  // loading (null) or forbidden (false)

  const p = state.prefs;
  const set = (patch) => setState((s) => ({ ...s, prefs: { ...s.prefs, ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...p, notify_email: (p.notify_email || "").trim() || null };
      const { data } = await api.put("/account/digest", body);
      setState((s) => ({ ...s, ...data, prefs: { ...data.prefs, timezone: p.timezone } }));
      toast.success("Digest preferences saved.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save preferences."); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/account/digest/test");
      toast.success(`Test digest sent to ${data.recipient}.`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(formatApiError(d) || "Could not send the test digest.");
    } finally { setTesting(false); }
  };

  return (
    <section className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="digest-settings">
      <div className="flex items-start gap-2">
        <span className="grid place-items-center w-8 h-8 rounded-lg bg-indigo-600 text-white mt-0.5"><Mail className="w-4 h-4" /></span>
        <div className="flex-1">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Sales digest</h2>
          <p className="text-sm text-slate-500">An optional email summary of your pipeline, verified payments and follow-ups — no need to log in.</p>
        </div>
        <Switch checked={!!p.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="digest-enabled" aria-label="Enable sales digest" />
      </div>

      {!state.email_configured && (
        <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="digest-provider-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Email delivery isn't configured yet (<code>EMERGENT_EMAIL_KEY</code>). You can set preferences now; digests will send once email is enabled.</span>
        </div>
      )}
      {!state.links_configured && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="digest-links-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Deep links are off until <code>APP_BASE_URL</code> is set to a valid https domain. Digests still send, without clickable links.</span>
        </div>
      )}

      <div className={`mt-2 transition-opacity ${p.enabled ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
        <Row title="Frequency" desc="Daily covers yesterday; weekly covers the last 7 days." testid="digest-row-frequency">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {["daily", "weekly"].map((f) => (
              <button key={f} type="button" data-testid={`digest-freq-${f}`} onClick={() => set({ frequency: f })}
                className={`px-3 py-1.5 text-sm font-medium capitalize ${p.frequency === f ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>{f}</button>
            ))}
          </div>
        </Row>

        <Row title="Delivery time" desc="Sent around this hour, in your time zone." testid="digest-row-hour">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            <select className={selectCls + " w-44"} value={p.send_hour} onChange={(e) => set({ send_hour: Number(e.target.value) })} data-testid="digest-hour">
              {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
          </div>
        </Row>

        {p.frequency === "weekly" && (
          <Row title="Day of week" testid="digest-row-weekday">
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-slate-400" />
              <select className={selectCls + " w-40"} value={p.weekly_day} onChange={(e) => set({ weekly_day: Number(e.target.value) })} data-testid="digest-weekday">
                {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
          </Row>
        )}

        <Row title="Time zone" testid="digest-row-timezone">
          <select className={selectCls + " w-56"} value={p.timezone} onChange={(e) => set({ timezone: e.target.value })} data-testid="digest-timezone">
            {!timezones.includes(p.timezone) && <option value={p.timezone}>{p.timezone}</option>}
            {timezones.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>)}
          </select>
        </Row>

        <Row title="Send to" desc={`Defaults to your account email. Current: ${state.recipient || "—"}`} testid="digest-row-email">
          <Input type="email" placeholder="Optional override email" value={p.notify_email || ""}
            onChange={(e) => set({ notify_email: e.target.value })} className="w-60" data-testid="digest-email" />
        </Row>

        <div className="mt-4 mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Include</div>
        <Row title="Sales performance summary" desc="Created, shared, viewed, accepted, verified revenue + pipeline totals." testid="digest-row-sales">
          <Switch checked={!!p.include_sales_summary} onCheckedChange={(v) => set({ include_sales_summary: v })} data-testid="digest-incl-sales" />
        </Row>
        <Row title="Follow-up reminders" desc="Awaiting payment, awaiting a decision, expiring soon." testid="digest-row-followups">
          <Switch checked={!!p.include_follow_ups} onCheckedChange={(v) => set({ include_follow_ups: v })} data-testid="digest-incl-followups" />
        </Row>
        <Row title="Consultation requests" desc="New consultation leads (platform admins only)." testid="digest-row-consultations">
          <Switch checked={!!p.include_consultations} onCheckedChange={(v) => set({ include_consultations: v })} data-testid="digest-incl-consultations" />
        </Row>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="digest-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save preferences"}
        </Button>
        <Button variant="outline" onClick={sendTest} disabled={testing || !state.email_configured} data-testid="digest-test">
          {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send me a test digest
        </Button>
      </div>

      <div className="mt-6 pt-6 border-t border-slate-100">
        <MonthlyRevenue editable />
      </div>
    </section>
  );
}
